import React, { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getProjects } from '../../api/projects'
import { getElectricalPanels } from '../../api/electricalPanels'
import { getSchemas } from '../../api/schemas'
import {
  getAdminUsers,
  updateAdminUser,
  getProjectAccess,
  upsertProjectAccess,
  deleteProjectAccessById,
  getUserPermissions,
  createPermission,
  deletePermissionById,
  AdminUser,
  ProjectAccess,
  PermissionRow,
} from '../../api/admin'
import { useAuth } from '../../AuthContext'

const DENY_PERMISSION = 'deny'
const NO_ACCESS = 'none'

// Все возможные состояния доступа
const ACCESS_OPTIONS = [
  { value: 'view', label: 'просмотр', icon: '👁️', description: 'Только просмотр' },
  { value: 'edit', label: 'редактирование', icon: '✏️', description: 'Просмотр и редактирование' },
  { value: 'manage', label: 'управление', icon: '⚙️', description: 'Полный доступ, включая удаление' },
  { value: DENY_PERMISSION, label: 'запрет', icon: '🚫', description: 'Запретить доступ (скрыть от пользователя)' },
]

interface HierarchicalPermission {
  projectId: number
  projectName: string
  projectRole: string | null
  panels: Array<{
    panelId: number
    panelName: string
    panelPermission: string | null
    schemas: Array<{
      schemaId: number
      schemaName: string
      schemaPermission: string | null
    }>
  }>
}

const AdminUserAccessPageHierarchical: React.FC = () => {
  const { user: currentUser } = useAuth()
  const params = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const userId = Number(params.userId)

  const { data: usersData } = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: getAdminUsers,
  })

  const targetUser: AdminUser | undefined = useMemo(
    () => usersData?.users.find((u) => u.id === userId),
    [usersData, userId],
  )

  const { data: projectsData } = useQuery({
    queryKey: ['projects'],
    queryFn: getProjects,
  })

  const { data: accessData } = useQuery({
    queryKey: ['admin', 'project-access', userId],
    queryFn: () => getProjectAccess(userId),
    enabled: userId > 0,
  })

  const projectAccess: ProjectAccess[] = useMemo(
    () => accessData?.access ?? [],
    [accessData],
  )

  // Load all permissions for all projects
  const { data: allPermissionsData } = useQuery({
    queryKey: ['admin', 'all-permissions', userId],
    queryFn: async () => {
      if (!projectsData) return { permissions: [] }
      const allPerms: PermissionRow[] = []
      for (const project of projectsData) {
        try {
          const result = await getUserPermissions(userId, project.id)
          allPerms.push(...result.permissions)
        } catch (error) {
          console.error(`Failed to load permissions for project ${project.id}:`, error)
        }
      }
      return { permissions: allPerms }
    },
    enabled: !!projectsData && projectsData.length > 0,
  })

  const allPermissions: PermissionRow[] = useMemo(
    () => allPermissionsData?.permissions ?? [],
    [allPermissionsData],
  )

  // Load panels and schemas for each project
  const panelsQueries = useQuery({
    queryKey: ['electrical-panels-all'],
    queryFn: async () => {
      if (!projectsData) return []
      const allPanels: Array<{ projectId: number; panels: any[] }> = []
      for (const project of projectsData) {
        try {
          const panels = await getElectricalPanels(project.id)
          allPanels.push({ projectId: project.id, panels })
        } catch (error) {
          console.error(`Failed to load panels for project ${project.id}:`, error)
        }
      }
      return allPanels
    },
    enabled: !!projectsData && projectsData.length > 0,
  })

  const schemasQueries = useQuery({
    queryKey: ['schemas-all'],
    queryFn: async () => {
      if (!projectsData) return []
      const allSchemas: Array<{ projectId: number; schemas: any[] }> = []
      for (const project of projectsData) {
        try {
          const schemas = await getSchemas(project.id)
          allSchemas.push({ projectId: project.id, schemas })
        } catch (error) {
          console.error(`Failed to load schemas for project ${project.id}:`, error)
        }
      }
      return allSchemas
    },
    enabled: !!projectsData && projectsData.length > 0,
  })

  // Build complete hierarchical structure
  const completeHierarchicalData: HierarchicalPermission[] = useMemo(() => {
    if (!projectsData || !panelsQueries.data || !schemasQueries.data) return []

    return projectsData.map((project) => {
      const access = projectAccess.find((a) => a.project_id === project.id)
      const projectRole = access ? access.role_in_project : null

      const panelsData = panelsQueries.data.find((p) => p.projectId === project.id)
      const schemasData = schemasQueries.data.find((s) => s.projectId === project.id)

      const panels = (panelsData?.panels ?? []).map((panel) => {
        // Найти все permissions для этого электрощита (кроме deny)
        const panelPerms = allPermissions.filter(
          (p) =>
            p.project_id === project.id &&
            p.resource_type === 'electrical_panel' &&
            p.resource_id === String(panel.id) &&
            p.permission !== DENY_PERMISSION,
        )
        // Выбрать самый высокий уровень (manage > edit > view)
        const permissionPriority: Record<string, number> = { view: 1, edit: 2, manage: 3 }
        const panelPerm = panelPerms.length > 0
          ? panelPerms.reduce((highest, current) => {
              const currentPriority = permissionPriority[current.permission] || 0
              const highestPriority = permissionPriority[highest.permission] || 0
              return currentPriority > highestPriority ? current : highest
            })
          : null

        const panelSchemas = (schemasData?.schemas ?? [])
          .filter((schema) => schema.electrical_panel_id === panel.id)
          .map((schema) => {
            // Найти все permissions для этой схемы (кроме deny)
            const schemaPerms = allPermissions.filter(
              (p) =>
                p.project_id === project.id &&
                p.resource_type === 'schema' &&
                p.resource_id === String(schema.id) &&
                p.permission !== DENY_PERMISSION,
            )
            // Выбрать самый высокий уровень
            const schemaPerm = schemaPerms.length > 0
              ? schemaPerms.reduce((highest, current) => {
                  const currentPriority = permissionPriority[current.permission] || 0
                  const highestPriority = permissionPriority[highest.permission] || 0
                  return currentPriority > highestPriority ? current : highest
                })
              : null
            return {
              schemaId: schema.id,
              schemaName: schema.name,
              schemaPermission: schemaPerm ? schemaPerm.permission : null,
            }
          })

        return {
          panelId: panel.id,
          panelName: panel.name,
          panelPermission: panelPerm ? panelPerm.permission : null,
          schemas: panelSchemas,
        }
      })

      return {
        projectId: project.id,
        projectName: project.name,
        projectRole,
        panels,
      }
    })
  }, [projectsData, projectAccess, panelsQueries.data, schemasQueries.data, allPermissions])

  const upsertAccessMutation = useMutation({
    mutationFn: (payload: { projectId: number; role: string | null }) => {
      if (payload.role === null) {
        const access = projectAccess.find((a) => a.project_id === payload.projectId)
        if (access) {
          return deleteProjectAccessById(access.id)
        }
        return Promise.resolve()
      }
      return upsertProjectAccess(userId, payload.projectId, payload.role)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'project-access', userId] })
    },
  })

  const aiModesMutation = useMutation<void, unknown, string[]>({
    mutationFn: async (modes) => {
      await updateAdminUser(userId, { ai_modes: modes })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
    },
  })

  // Функция для переключения режима ИИ
  const handleToggleAiMode = (mode: string) => {
    if (!targetUser) return
    const currentModes = targetUser.ai_modes || []
    let newModes: string[]
    if (currentModes.includes(mode)) {
      newModes = currentModes.filter((m) => m !== mode)
    } else {
      newModes = [...currentModes, mode]
    }
    // Если все галочки сняты, отправляем пустой массив (на бэкенде это преобразуется в 'none')
    aiModesMutation.mutate(newModes)
  }

  const permissionMutation = useMutation<void, unknown, {
    projectId: number
    resourceType: string
    resourceId: string | null
    selectedValue: string
  }>({
    mutationFn: async (payload) => {
      // Найти все существующие permissions для этого ресурса
      const existingPermissions = allPermissions.filter(
        (p) =>
          p.project_id === payload.projectId &&
          p.resource_type === payload.resourceType &&
          p.resource_id === payload.resourceId,
      )

      // Удаляем все существующие permissions
      const deletePromises = existingPermissions.map((p) => deletePermissionById(p.id))
      await Promise.all(deletePromises)

      // Создаём новый permission
      try {
        await createPermission({
          user_id: userId,
          project_id: payload.projectId,
          resource_type: payload.resourceType,
          resource_id: payload.resourceId,
          permission: payload.selectedValue,
        })
      } catch (error) {
        console.error('Failed to create permission:', error)
        throw error
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'all-permissions', userId] })
    },
    onError: (error) => {
      console.error('Permission mutation error:', error)
      alert('Failed to update permission. Check console for details.')
    },
  })

  // Helper to get effective permission (with inheritance)
  const getEffectivePermission = (
    projectId: number,
    resourceType: 'electrical_panel' | 'schema',
    resourceId: number | null,
  ): string | null => {
    // Check explicit permission (including deny)
    const explicit = allPermissions.find(
      (p) =>
        p.project_id === projectId &&
        p.resource_type === resourceType &&
        p.resource_id === (resourceId ? String(resourceId) : null),
    )
    if (explicit) {
      return explicit.permission
    }

    // Check inheritance from project role
    const access = projectAccess.find((a) => a.project_id === projectId)
    if (access && access.role_in_project) {
      // Map role to permission: viewer -> view, editor -> edit, project_admin -> manage
      const roleMap: Record<string, string> = {
        viewer: 'view',
        editor: 'edit',
        project_admin: 'manage',
      }
      return roleMap[access.role_in_project] || null
    }

    return null
  }

  // Helper to check if resource is denied
  const isDenied = (
    projectId: number,
    resourceType: 'electrical_panel' | 'schema',
    resourceId: number | null,
  ): boolean => {
    const explicit = allPermissions.find(
      (p) =>
        p.project_id === projectId &&
        p.resource_type === resourceType &&
        p.resource_id === (resourceId ? String(resourceId) : null) &&
        p.permission === DENY_PERMISSION,
    )
    return !!explicit
  }

  if (!currentUser || currentUser.role !== 'system_admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white rounded shadow-md p-6 max-w-md w-full text-center">
          <h1 className="text-xl font-semibold mb-2">Доступ запрещён</h1>
          <p className="text-sm text-gray-600 mb-4">Только системные администраторы могут получить доступ к этой странице.</p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            На главную
          </button>
        </div>
      </div>
    )
  }

  if (!targetUser || !projectsData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white rounded shadow-md p-6 max-w-md w-full text-center">
          <p className="text-sm text-gray-600">Загрузка прав доступа пользователя...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <main className="container mx-auto px-4 py-6 max-w-7xl">
        <div className="mb-6">
          <button
            onClick={() => navigate('/admin/users')}
            className="text-blue-600 hover:text-blue-800 mb-2 text-sm"
          >
            ← Назад к пользователям
          </button>
          <h1 className="text-2xl font-bold text-gray-900">
            Управление доступом: {targetUser.name} ({targetUser.email})
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Иерархические права: Проекты → Электрощиты → Схемы
          </p>
        </div>

        <div className="bg-white rounded shadow-md p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">🤖 Доступ к веб-ИИ-агенту</h2>
          <div className="flex gap-4">
            {[
              { id: 'question', label: 'Вопрос (question)', desc: 'Только анализ и ответы' },
              { id: 'prepare', label: 'Подготовка (prepare)', desc: 'Подготовка плана изменений' },
              { id: 'apply', label: 'Применение (apply)', desc: 'Выполнение изменений' },
            ].map((mode) => {
              const isChecked = targetUser.ai_modes?.includes(mode.id) ?? false
              return (
                <label key={mode.id} className="flex items-center gap-2 cursor-pointer p-2 border rounded hover:bg-gray-50 transition-colors" title={mode.desc}>
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => handleToggleAiMode(mode.id)}
                    disabled={aiModesMutation.isPending || targetUser.role === 'system_admin'}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm font-medium text-gray-700">{mode.label}</span>
                </label>
              )
            })}
          </div>
          {targetUser.role === 'system_admin' && (
            <p className="text-xs text-gray-500 mt-2 italic">У суперадмина нет ограничений (галочки отключены).</p>
          )}
        </div>

        <div className="bg-white rounded shadow-md p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">📁 Доступ к проектам и схемам</h2>
          <div className="space-y-6">
            {completeHierarchicalData.map((project) => (
              <div key={project.projectId} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-semibold text-gray-900">
                    📁 Проект {project.projectId}: {project.projectName}
                  </h2>
                  <select
                    className="border rounded px-2 py-1 text-xs"
                    value={project.projectRole || 'none'}
                    onChange={(e) => {
                      const role = e.target.value === 'none' ? null : e.target.value
                      upsertAccessMutation.mutate({ projectId: project.projectId, role })
                    }}
                    disabled={upsertAccessMutation.isPending}
                  >
                    <option value="none">нет доступа</option>
                    <option value="viewer">просмотр (наследуется: просмотр)</option>
                    <option value="editor">редактирование (наследуется: редактирование)</option>
                    <option value="project_admin">администратор проекта (наследуется: управление)</option>
                  </select>
                </div>

                {project.panels.length === 0 ? (
                  <p className="text-xs text-gray-500 italic ml-4">Нет электрощитов</p>
                ) : (
                  <div className="ml-4 space-y-3">
                    {project.panels.map((panel) => {
                      const effectivePanelPerm = getEffectivePermission(
                        project.projectId,
                        'electrical_panel',
                        panel.panelId,
                      )
                      const denied = isDenied(
                        project.projectId,
                        'electrical_panel',
                        panel.panelId,
                      )
                      
                      // Определяем текущее выбранное значение
                      let currentValue: string | null = null
                      if (denied) {
                        currentValue = DENY_PERMISSION
                      } else if (panel.panelPermission) {
                        currentValue = panel.panelPermission
                      } else if (effectivePanelPerm) {
                        currentValue = effectivePanelPerm
                      }
                      // Если нет явного permission и нет наследования, показываем первое доступное значение
                      if (!currentValue) {
                        currentValue = ACCESS_OPTIONS[0].value
                      }

                      return (
                        <div key={panel.panelId} className="border-l-2 border-gray-300 pl-3">
                          <div className="flex items-center justify-between mb-2 gap-4">
                            <h3 className="text-sm font-medium text-gray-800 flex-shrink-0">
                              ⚡ {panel.panelName}
                            </h3>
                            <div className="flex gap-3 items-center flex-wrap">
                              {ACCESS_OPTIONS.map((option) => (
                                <label
                                  key={option.value}
                                  className="flex items-center gap-1.5 text-xs cursor-pointer hover:bg-gray-50 px-2 py-1 rounded transition-colors"
                                  title={option.description}
                                >
                                  <input
                                    type="radio"
                                    name={`panel-${panel.panelId}`}
                                    value={option.value}
                                    checked={currentValue === option.value}
                                    onChange={(e) => {
                                      permissionMutation.mutate({
                                        projectId: project.projectId,
                                        resourceType: 'electrical_panel',
                                        resourceId: String(panel.panelId),
                                        selectedValue: e.target.value,
                                      })
                                    }}
                                    disabled={permissionMutation.isPending}
                                    className="cursor-pointer"
                                  />
                                  <span className={option.icon}>{option.icon}</span>
                                  <span className={`${
                                    option.value === DENY_PERMISSION ? 'text-red-600 font-semibold' : 'text-gray-700'
                                  }`}>
                                    {option.label}
                                  </span>
                                </label>
                              ))}
                            </div>
                          </div>

                          {panel.schemas.length === 0 ? (
                            <p className="text-xs text-gray-500 italic ml-4">Нет схем</p>
                          ) : (
                            <div className="ml-4 space-y-2">
                              {panel.schemas.map((schema) => {
                                const effectiveSchemaPerm = getEffectivePermission(
                                  project.projectId,
                                  'schema',
                                  schema.schemaId,
                                )
                                const denied = isDenied(
                                  project.projectId,
                                  'schema',
                                  schema.schemaId,
                                )
                                
                                // Определяем текущее выбранное значение
                                let currentValue: string | null = null
                                if (denied) {
                                  currentValue = DENY_PERMISSION
                                } else if (schema.schemaPermission) {
                                  currentValue = schema.schemaPermission
                                } else if (effectiveSchemaPerm) {
                                  currentValue = effectiveSchemaPerm
                                }
                                // Если нет явного permission и нет наследования, показываем первое доступное значение
                                if (!currentValue) {
                                  currentValue = ACCESS_OPTIONS[0].value
                                }

                                return (
                                  <div
                                    key={schema.schemaId}
                                    className="border-l-2 border-blue-200 pl-2 py-1"
                                  >
                                    <div className="flex items-center justify-between gap-4">
                                      <span className="text-xs text-gray-700 flex-shrink-0">
                                        📄 {schema.schemaName}
                                      </span>
                                      <div className="flex gap-3 items-center flex-wrap">
                                        {ACCESS_OPTIONS.map((option) => (
                                          <label
                                            key={option.value}
                                            className="flex items-center gap-1.5 text-xs cursor-pointer hover:bg-gray-50 px-2 py-1 rounded transition-colors"
                                            title={option.description}
                                          >
                                            <input
                                              type="radio"
                                              name={`schema-${schema.schemaId}`}
                                              value={option.value}
                                              checked={currentValue === option.value}
                                              onChange={(e) => {
                                                permissionMutation.mutate({
                                                  projectId: project.projectId,
                                                  resourceType: 'schema',
                                                  resourceId: String(schema.schemaId),
                                                  selectedValue: e.target.value,
                                                })
                                              }}
                                              disabled={permissionMutation.isPending}
                                              className="cursor-pointer"
                                            />
                                            <span className={option.icon}>{option.icon}</span>
                                            <span className={`${
                                              option.value === DENY_PERMISSION ? 'text-red-600 font-semibold' : 
                                              option.value === NO_ACCESS ? 'text-blue-600' : 'text-gray-700'
                                            }`}>
                                              {option.label}
                                            </span>
                                          </label>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>

          {permissionMutation.isPending && (
            <div className="mt-4 text-xs text-gray-500">Сохранение прав доступа...</div>
          )}
        </div>

        <div className="mt-6 text-xs text-gray-500 bg-blue-50 p-4 rounded">
          <p className="font-semibold mb-2">Как работают права доступа:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>
              <strong>Роль в проекте</strong> (просмотр/редактирование/администратор проекта) автоматически даёт
              просмотр/редактирование/управление всем электрощитам и схемам внутри
            </li>
            <li>
              Можно <strong>переопределить</strong> права на конкретных электрощитах или схемах
            </li>
            <li>
              Radio buttons показывают <strong>текущее</strong> состояние доступа (наследованное или явное)
            </li>
            <li>
              Выбор <strong>"запрет"</strong> полностью скрывает ресурс от пользователя, даже если есть наследование
            </li>
          </ul>
        </div>
      </main>
    </div>
  )
}

export default AdminUserAccessPageHierarchical

